import { EyeIcon, Phone } from "@/components/icons";
import { Button } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { CustomJobListItemDTO } from "@/modules/custom-jobs/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import { isCustomJobOverdue } from "./job-status";

type CustomJobKanbanCardProps = {
  job: CustomJobListItemDTO;
  canManage: boolean;
  onView: (id: string) => void;
  onConfirm?: (id: string) => void;
  onStartWork?: (id: string) => void;
  onMarkReady?: (id: string) => void;
  onComplete?: (id: string) => void;
  isConfirmPending?: boolean;
  isStartPending?: boolean;
  isMarkReadyPending?: boolean;
  isCompletePending?: boolean;
};

export function CustomJobKanbanCard({
  job,
  canManage,
  onView,
  onConfirm,
  onStartWork,
  onMarkReady,
  onComplete,
  isConfirmPending,
  isStartPending,
  isMarkReadyPending,
  isCompletePending,
}: CustomJobKanbanCardProps) {
  const overdue = isCustomJobOverdue(job.dueDate, job.status);

  return (
    <article
      className={cn(
        "brand-card flex flex-col gap-3 p-4",
        overdue && "border-destructive/30 bg-destructive/2",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-base text-foreground">
            {job.customerName}
          </p>
          {job.customerPhone ? (
            <p className="flex items-center gap-1 text-muted-foreground text-xs">
              <Phone className="size-3.5 shrink-0" />
              {job.customerPhone}
            </p>
          ) : null}
          <p className="mt-1 font-mono text-muted-foreground text-xs">
            {job.jobNumber}
          </p>
        </div>
        <span className="shrink-0 font-display text-foreground tabular-nums">
          {formatLak(job.total)}
        </span>
      </div>

      <div className="text-muted-foreground text-xs leading-relaxed">
        <p>{job.itemCount} ລາຍການ</p>
        {job.dueDate ? <p>ນັດ {formatDateLocal(job.dueDate)}</p> : null}
      </div>

      <div className="flex flex-wrap gap-2 border-secondary/15 border-t pt-3">
        <Button
          variant="outline"
          size="sm"
          className="min-h-10 rounded-full"
          onClick={() => onView(job.id)}
        >
          <EyeIcon data-icon="inline-start" />
          ລາຍລະອຽດ
        </Button>
        {canManage && job.status === "pending_confirm" && onConfirm ? (
          <Button
            size="sm"
            className="min-h-10 flex-1 rounded-full"
            isLoading={isConfirmPending}
            onClick={() => onConfirm(job.id)}
          >
            ຢືນຢັນ
          </Button>
        ) : null}
        {canManage && job.status === "queued" && onStartWork ? (
          <Button
            size="sm"
            className="min-h-10 flex-1 rounded-full"
            isLoading={isStartPending}
            onClick={() => onStartWork(job.id)}
          >
            ເລີ່ມເຮັດ
          </Button>
        ) : null}
        {canManage && job.status === "in_progress" && onMarkReady ? (
          <Button
            size="sm"
            className="min-h-10 flex-1 rounded-full"
            isLoading={isMarkReadyPending}
            onClick={() => onMarkReady(job.id)}
          >
            ພ້ອມຮັບ
          </Button>
        ) : null}
        {canManage && job.status === "ready" && onComplete ? (
          <Button
            size="sm"
            className="min-h-10 flex-1 rounded-full"
            isLoading={isCompletePending}
            onClick={() => onComplete(job.id)}
          >
            ສົ່ງມອບ+ຮັບເງິນ
          </Button>
        ) : null}
      </div>
    </article>
  );
}
